/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import EpkHeader from '../components/EpkView/EpkHeader/EpkHeader';
import EpkCover from '../components/EpkView/EpkCover/EpkCover';
import EpkSocialAction from '../components/EpkView/EpkSocialAction/EpkSocialAction';
import EpkDetail from '../components/EpkView/EpkDetail/EpkDetail';
import EpkSynopsis from '../components/EpkView/EpkSynopsis/EpkSynopsis';
import EpkUniqueness from '../components/EpkView/EpkUniqueness/EpkUniqueness';
import EpkCast from '../components/EpkView/EpkCast/EpkCast';
import EpkWorker from '../components/EpkView/EpkWorker/EpkWorker';
import EpkResources from '../components/EpkView/EpkResources/EpkResources';
import EpkVideoGallery from '../components/EpkView/EpkVideoGallery/EpkVideoGallery';
import EpkAward from '../components/EpkView/EpkAward/EpkAward';
import EpkSupporters from '../components/EpkView/EpkSupporters/EpkSupporters';
import EpkWaitlist from '../components/EpkView/EpkWaitlist/EpkWaitlist';
import DonationModal from '../components/donate/DonationModal';
import RequestAccessModal from '../components/common/Modals/RequestAccessModal';
import LoginModal from '../components/common/Modals/LoginModal';
import NewMessageModal from '../components/common/Modals/NewMessageModal';
import { getFepksById, getMyRequests, updateFepk, uploadSingleFile } from '../api/epks';
import { useSelector } from 'react-redux';
import { FepkContext } from '../context/FepkContext';
import Banner from '../components/EpkView/EpkBanner/EpkBanner';
import emptyBanner from '../images/empty_banner.jpeg';
import EpkSalesCalculator from '../components/EpkView/EpkSalesCalculator/EpkSalesCaculator';
import EpkPhotoGallery from '../components/EpkView/EpkPhotoGallery/EpkPhotoGallery';
import AnalyticsDataService from "../api/analytics";
import EditNavBar from '../components/navbar/EditNavBar';
import { resolveOrder } from '../config/epkSections';
import ConfirmDialog from '../components/common/Modals/ConfirmDialog';

function EpkViewPage() {
  const { t } = useTranslation();
  const { fepkId, setFepkId, fepkMaker, setFepkMaker, setEpkCollaborators } = React.useContext(FepkContext);
  const user = useSelector((state) => state.user);
  // False until AuthBootstrap's silent refresh resolves. Guards the edit-mode
  // ownership check below so it never runs on an indeterminate session.
  const authChecked = useSelector((state) => state.authStatus.checked);

  // States
  const [epkInfo, setEpkInfo] = useState();
  usePageTitle(epkInfo?.title);
  const [requestStatus, setRequestStatus] = useState();
  const [refresh, setRefresh] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [globalTotalReach, setGlobalTotalReach] = useState(0);
  
  let { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [draftEpk, setDraftEpk] = useState(null);
  // Any edit to the draft marks it dirty. Only used to decide whether leaving the
  // editor needs an "unsaved changes" confirmation -- not a deep diff against epkInfo.
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [activeSection, setActiveSection] = useState('cover');
  const [isSaving, setIsSaving] = useState(false);

  const [pendingDeletes, setPendingDeletes] = useState([]);
  
  // Validation States
  const [errors, setErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationTarget, setValidationTarget] = useState(null);

  const coverRef = useRef(null);
  const detailsRef = useRef(null);
  const synopsisRef = useRef(null);
  const castsRef = useRef(null);     
  const crewsRef = useRef(null);
  const uniquenesssRef = useRef(null);    
  const mediaRef = useRef(null);     
  const resourcesRef = useRef(null);
  const buzzRef = useRef(null);
  const supportersRef = useRef(null);
  const waitlistRef = useRef(null);
  const photoOnlyRef = useRef(null);
  const videoOnlyRef = useRef(null); 

  // activeData / order computed early: the scroll-spy and the section render
  // below both need to read the SAME resolved order, or they drift.
  const activeData = isEditMode && draftEpk ? draftEpk : epkInfo;
  const order = resolveOrder(activeData?.sectionOrder);
  const orderKey = order.join(',');

  const sectionRefs = {
    cover: coverRef,
    details: detailsRef,
    synopsis: synopsisRef,
    uniqueness: uniquenesssRef,
    casts: castsRef,
    crews: crewsRef,
    media: mediaRef,
    resources: resourcesRef,
    buzz: buzzRef,
    supporters: supportersRef,
    waitlist: waitlistRef
  };

  useEffect(() => {
    if (!isEditMode) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      let currentSection = 'cover';

      const sections = [
        { id: 'cover', ref: coverRef },
        ...order.map((id) => ({ id, ref: sectionRefs[id] }))
      ];

      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sections[i].ref.current;
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= scrollPosition) {
            currentSection = sections[i].id;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, orderKey]);

  const scrollToSection = (sectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref && ref.current) {
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToPhotos = () => {
    if (photoOnlyRef.current) {
      const y = photoOnlyRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  const scrollToVideos = () => {
    if (videoOnlyRef.current) {
      const y = videoOnlyRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

   const handleClose = (modalType) => {
    if (user) {
      switch (modalType) {
        case 'message': setShowMessageModal(false); break;
        case 'request': setShowRequestAccessModal(false); break;
        case 'wish_to_donate': setShowDonationModal(false); break;
        default: break;
      }
    } else {
      setShowLoginModal(false);
    }
  };

  const handleShow = (modalType) => {
    if (modalType === 'login') {
      setShowLoginModal(true);
    } else if (user) {
      switch (modalType) {
        case 'message': setShowMessageModal(true); break;
        case 'request': setShowRequestAccessModal(true); break;
        case 'wish_to_donate': setShowDonationModal(true); break;
        default: break;
      }
    } else {
      setShowLoginModal(true);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setIsEditMode(queryParams.get("edit") === "true");
    const refCode = queryParams.get("ref");
    if (refCode) {
      const storedKey = `waitlist_ref_${id}`;
      if (!localStorage.getItem(storedKey)) {
        localStorage.setItem(storedKey, refCode);
      }
    }
  }, [location.search, id]);

  useEffect(() => {
    if (isEditMode && epkInfo && !draftEpk) {
      setDraftEpk(JSON.parse(JSON.stringify(epkInfo)));
      setIsDirty(false);
    }
    if (!isEditMode && draftEpk) {
      setDraftEpk(null);
      setIsDirty(false);
      setErrors({});
      setPendingDeletes([]);
    }
  }, [isEditMode, epkInfo, draftEpk]);

  useEffect(() => {
    getFepksById(id).then((res) => {
      if (res.message) return console.error(res.message);
      setEpkInfo(res);
      setFepkId(res._id);
      setFepkMaker(res.film_maker);
      if (setEpkCollaborators) setEpkCollaborators(res.collaborators || []);

      const filmMakerId = res.film_maker?._id ?? res.film_maker;
      const owner = String(filmMakerId) === String(user?.id);

      // The edit-mode ownership check deliberately does NOT live here. It needs to
      // wait for the session, which this effect cannot do. See the effect below.

      if (owner) {
        setRequestStatus('approved');
      } else if (user?.id) {
        // The public EPK no longer lists requests (KIN-91); ask for our own.
        getMyRequests(user.id)
          .then((list) => {
            const myRequest = list.find((e) => e._id === res._id)?.requests?.[0];
            if (myRequest) setRequestStatus(myRequest.status);
          })
          .catch(() => {});
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh, user?.id, setFepkId, setFepkMaker, setEpkCollaborators]);

  // Ownership is INDETERMINATE until AuthBootstrap's silent refresh lands: the access
  // token is not persisted (store.js blacklists "user"), so on a cold load of an
  // /epk/:id?edit=true URL Redux has no user yet. Deciding here before authChecked
  // treats "not loaded yet" as "not the owner" and bounces the real owner out of
  // their own edit URL on every refresh or deep link.
  //
  // This is a separate effect on purpose. Folding authChecked into the fetch effect
  // above would refetch the EPK a second time for every anonymous visitor once the
  // check resolves. draftEpk is already seeded by the isEditMode effect above, so
  // the only thing that has to wait for the session is the redirect itself.
  useEffect(() => {
    if (!isEditMode || !epkInfo || !authChecked) return;

    const filmMakerId = epkInfo.film_maker?._id ?? epkInfo.film_maker;
    const owner = String(filmMakerId) === String(user?.id);
    const collaborator = epkInfo.collaborators?.some(
      (c) => (c.user?._id ?? c.user) === user?.id
    );

    if (!owner && !collaborator) {
      navigate(`/epk/${epkInfo._id}`, { replace: true });
    }
  }, [isEditMode, epkInfo, user?.id, authChecked, navigate]);

  const posterImage = React.useMemo(() => {
    const img = epkInfo?.image_details;
    if (!img || img === '') return emptyBanner;
    if (img.startsWith('http')) return img;
    return `${process.env.REACT_APP_AWS_URL}/${img}`;
  }, [epkInfo?.image_details]);

  useEffect(() => {
    if (id && epkInfo && user?.id !== epkInfo.film_maker?._id) {
      AnalyticsDataService.trackView(id, 'EPK').catch(err => console.log("Analytics failed", err));
    }
  }, [id, epkInfo, user?.id]);

  const discardDraft = () => {
    setDraftEpk(null);
    setIsDirty(false);
    setErrors({});
    setPendingDeletes([]);
    setIsDirty(false);
    navigate(`/epk/${epkInfo._id}`, { replace: true });
  };

  const handleDiscard = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      discardDraft();
    }
  };

  const handleMarkMediaForDeletion = (mediaKey) => {
    if (!mediaKey) return;
    // External links and local previews are never S3 keys.
    if (/^(https?|blob|data):/i.test(mediaKey)) return;
    setPendingDeletes(prev => [...prev, mediaKey]);
    setIsDirty(true);
  };

  const handleSaveAndExit = async () => {
    const newErrors = {};
    
    if (!draftEpk.productionYear) newErrors.productionYear = true;
    if (!draftEpk.language) newErrors.language = true;
    if (!draftEpk.budget) newErrors.budget = true;
    if (!draftEpk.status) newErrors.status = true;
    if (!draftEpk.logLine_short || draftEpk.logLine_short.trim() === "") newErrors.logLine_short = true;
    if (!draftEpk.image_details && !draftEpk.new_poster_file) newErrors.image_details = true;
    
    const hasTrailer = !!draftEpk.trailer_url || !!draftEpk.new_trailer_file;
    const hasBanner = (draftEpk.banners && draftEpk.banners.length > 0) || !!draftEpk.banner_url || !!draftEpk.new_banner_file;
    if (!hasTrailer && !hasBanner) newErrors.trailerOrBanner = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      // Determine where to auto-scroll once they close the modal
      if (newErrors.image_details || newErrors.trailerOrBanner || newErrors.logLine_short) {
        setValidationTarget('cover');
      } else {
        setValidationTarget('details'); 
      }
      
      // Trigger new Modal instead of browser alert
      setShowValidationModal(true);
      return; 
    }

    setErrors({});
    setIsSaving(true);
    try {
      let finalDraft = { ...draftEpk };
      // A new banner or trailer thumbnail replaces the whole banners array;
      // the old banner keys go to the server, which keeps any still in use.
      let replacedBanners = [];

      delete finalDraft._id;
      delete finalDraft.__v;
      delete finalDraft.createdAt;
      delete finalDraft.updatedAt;

      if (finalDraft.film_maker && finalDraft.film_maker._id) finalDraft.film_maker = finalDraft.film_maker._id;
      if (finalDraft.crew && finalDraft.crew.length > 0) {
        finalDraft.crew = finalDraft.crew.map(member => ({ ...member, crewId: member.crewId?._id || member.crewId }));
      }
      if (finalDraft.actors && finalDraft.actors.length > 0) finalDraft.actors = finalDraft.actors.map(actor => actor._id || actor);
      if (finalDraft.likes) finalDraft.likes = finalDraft.likes.map(u => u._id || u);
      if (finalDraft.favourites) finalDraft.favourites = finalDraft.favourites.map(u => u._id || u);

      if (finalDraft.new_poster_file) {
        const posterKey = await uploadSingleFile(finalDraft.new_poster_file, user?.token);
        finalDraft.image_details = posterKey; 
        delete finalDraft.new_poster_file;   
      }

      if (finalDraft.new_banner_file) {
        const bannerKey = await uploadSingleFile(finalDraft.new_banner_file, user?.token);
        replacedBanners = (epkInfo.banners || []).map((b) => b.url);
        finalDraft.banners = [{ url: bannerKey, is_thumbnail: true }];
        delete finalDraft.new_banner_file;
      }

      if (finalDraft.new_trailer_file) {
        const trailerKey = await uploadSingleFile(finalDraft.new_trailer_file, user?.token);
        finalDraft.trailer_url = trailerKey; 
        delete finalDraft.new_trailer_file;
      }

      if (finalDraft.new_trailer_thumbnail) {
        const thumbKey = await uploadSingleFile(finalDraft.new_trailer_thumbnail, user?.token);
        replacedBanners = (epkInfo.banners || []).map((b) => b.url);
        finalDraft.banners = [{ url: thumbKey, is_thumbnail: true }];
        delete finalDraft.new_trailer_thumbnail;
      }

      // The server deletes removed media from S3 after the save lands, keeping
      // any key the saved EPK still uses (e.g. an old trailer moved to the library).
      await updateFepk(epkInfo._id, { ...finalDraft, mediaToDelete: [...pendingDeletes, ...replacedBanners], expectedUpdatedAt: draftEpk.updatedAt }, user?.token);
      setPendingDeletes([]);

      const freshEpkData = await getFepksById(epkInfo._id);
      setEpkInfo(freshEpkData);
      setDraftEpk(null);
      setIsDirty(false);
      setIsEditMode(false);
      setIsDirty(false);
      toast.success(t("EPK saved successfully!"));

      // sanitizeSupporters drops anything not in the active catalog and caps the rest,
      // and the save still returns 200. Without this the owner gets no signal that
      // some of their picks were not kept (a supporter deactivated while they were
      // editing is the realistic case).
      const submittedSupporters = Array.isArray(finalDraft.supporters) ? finalDraft.supporters.length : 0;
      const savedSupporters = Array.isArray(freshEpkData?.supporters) ? freshEpkData.supporters.length : 0;
      if (submittedSupporters > savedSupporters) {
        toast.warn(
          t("Some supporters were not saved. Only supporters in the KinoKlik catalog can be displayed.")
        );
      }
      navigate(`/epk/${epkInfo._id}`, { replace: true });

    } catch (error) {
      console.error("Save process failed:", error);
      // 409 + stale: someone saved after this draft was loaded (KIN-125).
      if (error.response?.status === 409 && error.response?.data?.stale) {
        setShowConflictModal(true);
        return;
      }
      toast.error(
        error.response?.status === 400
          ? t("Some fields are invalid, so your changes were not saved.")
          : t("Failed to save changes. Please try again.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  // The new ClearError function to be passed down
  const clearError = (field) => {
    setErrors(prev => {
      const newErrs = { ...prev };
      delete newErrs[field];
      if (['new_poster_file', 'image_details'].includes(field)) delete newErrs.image_details;
      if (['new_trailer_file', 'new_banner_file', 'trailer_url', 'banner_url'].includes(field)) delete newErrs.trailerOrBanner;
      return newErrs;
    });
  };

  const handleFieldChange = (field, value) => {
    clearError(field);
    setIsDirty(true);
    // A function value updates from the latest draft (used after slow uploads). It is dropped once the
    // draft is gone (saved or discarded) instead of resurrecting a one-field draft.
    setDraftEpk((prev) => typeof value === "function"
      ? (prev ? { ...prev, [field]: value(prev[field]) } : prev)
      : { ...prev, [field]: value });
    setIsDirty(true);
  };

  // Registry: id -> render fn. The only thing that changes per-section is
  // WHERE it renders (see `order` below); the props each receives are the
  // same shared props used everywhere else on the page.
  const sectionRegistry = {
    details: () => (
      <EpkDetail
        epkInfo={activeData}
        isEditMode={isEditMode}
        onChange={handleFieldChange}
        errors={errors}
        clearError={clearError}
      />
    ),
    synopsis: () => (
      <EpkSynopsis epkInfo={activeData} requestStatus={requestStatus} handler={handleShow} isEditMode={isEditMode} onChange={handleFieldChange} />
    ),
    uniqueness: () => (
      <EpkUniqueness epkInfo={activeData} requestStatus={requestStatus} handler={handleShow} isEditMode={isEditMode} onChange={handleFieldChange} />
    ),
    casts: () => (
      <EpkCast epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} />
    ),
    crews: () => (
      <EpkWorker epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} />
    ),
    media: () => (
      <>
        <div ref={photoOnlyRef}>
          <EpkPhotoGallery epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} onMarkMediaForDeletion={handleMarkMediaForDeletion} />
        </div>
        <div ref={videoOnlyRef}>
          <EpkVideoGallery epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} onMarkMediaForDeletion={handleMarkMediaForDeletion} />
        </div>
      </>
    ),
    resources: () => (
      <EpkResources epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} onMarkMediaForDeletion={handleMarkMediaForDeletion} />
    ),
    buzz: () => (
      <EpkAward epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} onMarkMediaForDeletion={handleMarkMediaForDeletion} />
    ),
    supporters: () => (
      <EpkSupporters epkInfo={activeData} isEditMode={isEditMode} onChange={handleFieldChange} />
    ),
    waitlist: () => (
      <EpkWaitlist epkInfo={activeData} isEditMode={isEditMode} isDirty={isDirty} onChange={handleFieldChange} />
    ),
  };

  return (
    epkInfo && (
      <div className="tw-flex tw-justify-center tw-overflow-x-hidden tw-bg-[#1E0039] tw-relative">

        {isEditMode && (
          <EditNavBar
            activeSection={activeSection}
            onSectionClick={scrollToSection}
            onDiscard={handleDiscard}
            onSave={handleSaveAndExit}
            isSaving={isSaving}
            sectionOrder={activeData?.sectionOrder}
            onReorderApply={(nextOrder) => handleFieldChange('sectionOrder', nextOrder)}
          />
         )}



        <div
          className={`tw-w-11/12 ${isEditMode ? 'tw-mt-[110px]' : ''}`}
          onChange={isEditMode ? () => setIsDirty(true) : undefined}
        >

          <div ref={coverRef}>
            <EpkHeader epkInfo={activeData} setGlobalTotalReach={setGlobalTotalReach} isEditMode={isEditMode} onChange={handleFieldChange} clearError={clearError} errors={errors} />
            <EpkCover epkInfo={activeData} scrollToPhotos={scrollToPhotos} scrollToVideos={scrollToVideos} isEditMode={isEditMode} onChange={handleFieldChange} clearError={clearError} errors={errors} />
          </div>

          <EpkSocialAction epkInfo={activeData} handler={handleShow} showDonationModal={showDonationModal} setShowDonationModal={setShowDonationModal} isEditMode={isEditMode} onChange={handleFieldChange} />

          {order.map((sectionId) => (
            <div ref={sectionRefs[sectionId]} key={sectionId}>
              {sectionRegistry[sectionId] ? sectionRegistry[sectionId]() : null}
            </div>
          ))}

          {showRequestAccessModal && <RequestAccessModal close={handleClose} open={handleShow} epkId={epkInfo._id} filmmakerId={epkInfo.film_maker._id} user={user} setRefresh={setRefresh}/>}
          {showLoginModal && <LoginModal close={handleClose} open={handleShow} epkId={epkInfo._id} filmmakerId={epkInfo.film_maker._id} user={user} setRefresh={setRefresh}/>}
          {showMessageModal && <NewMessageModal close={handleClose} open={handleShow} epkId={epkInfo._id} filmmakerId={epkInfo.film_maker._id} user={user} setRefresh={setRefresh}/>}
          {showDonationModal && <DonationModal isOpen={showDonationModal} onRequestClose={() => setShowDonationModal(false)} epkId={epkInfo._id} userId={user.id} epkImage={posterImage} epkDonatePayPal={epkInfo.DonatePayPal_url} epkDonateStripe={epkInfo.DonateStripe_url} />}

          <EpkSalesCalculator globalTotalReach={globalTotalReach} />
          <Banner />
        </div>

        <ConfirmDialog
          isOpen={showDiscardModal}
          variant="discard"
          title={t("Discard Changes?")}
          confirmLabel={t("Discard")}
          cancelLabel={t("Keep Editing")}
          onConfirm={() => { setShowDiscardModal(false); discardDraft(); }}
          onCancel={() => setShowDiscardModal(false)}
        >
          {t("You have unsaved changes. Are you sure you want to discard them? All your progress will be lost.")}
        </ConfirmDialog>

        <ConfirmDialog
          isOpen={showConflictModal}
          variant="discard"
          title={t("This page was changed")}
          confirmLabel={t("Reload latest version")}
          cancelLabel={t("Keep Editing")}
          onConfirm={() => window.location.reload()}
          onCancel={() => setShowConflictModal(false)}
        >
          {t("Someone else, or another tab, saved changes after you started editing. Your changes were not saved. Reload to see the latest version, then make your edits again.")}
        </ConfirmDialog>

        {/* --- CUSTOM VALIDATION ERROR MODAL --- */}
        {showValidationModal && (
          <div className="tw-fixed tw-inset-0 tw-z-[1060] tw-flex tw-items-center tw-justify-center tw-bg-[#0a0014]/90 tw-backdrop-blur-sm tw-p-4">
            <div className="tw-bg-[#280D41] tw-border tw-border-red-500/50 tw-rounded-2xl tw-p-6 md:tw-p-8 tw-max-w-md tw-w-full tw-shadow-[0_20px_50px_rgba(239,68,68,0.2)]">
              <h3 className="tw-text-white tw-text-xl md:tw-text-2xl tw-font-bold tw-mb-4 tw-font-['Plus_Jakarta_Sans']">
                {t("Missing Information")}
              </h3>
              <p className="tw-text-[#E2BDC9] tw-text-sm tw-mb-6 tw-leading-relaxed">
                {t("Please complete the following required fields before publishing your EPK:")}
              </p>

              <ul className="tw-list-disc tw-list-inside tw-text-white tw-font-bold tw-text-sm tw-mb-8 tw-space-y-2 tw-bg-[#1E0039] tw-p-4 tw-rounded-xl">
                {errors.logLine_short && <li className="tw-text-red-400">{t("Logline")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Cover Section)")}</span></li>}
                {errors.image_details && <li className="tw-text-red-400">{t("Cover Poster")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Cover Section)")}</span></li>}
                {errors.trailerOrBanner && <li className="tw-text-red-400">{t("Trailer or Banner")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Cover Section)")}</span></li>}
                {errors.productionYear && <li className="tw-text-red-400">{t("Production Year")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Details Section)")}</span></li>}
                {errors.language && <li className="tw-text-red-400">{t("Language")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Details Section)")}</span></li>}
                {errors.budget && <li className="tw-text-red-400">{t("Budget")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Details Section)")}</span></li>}
                {errors.status && <li className="tw-text-red-400">{t("Status")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal">{t("(Details Section)")}</span></li>}
              </ul>

              <div className="tw-flex tw-justify-end">
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    if (validationTarget) scrollToSection(validationTarget);
                  }}
                  className="tw-px-6 tw-py-2.5 tw-bg-red-500 hover:tw-bg-red-600 tw-rounded-lg tw-text-white tw-font-bold tw-text-sm tw-uppercase tw-tracking-widest tw-border-none tw-shadow-[0_0_15px_rgba(239,68,68,0.4)] tw-transition-colors tw-cursor-pointer"
                >
                  {t("Review Fields")}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    )
  );
}
export default EpkViewPage;
